import React, { useState, useRef, useEffect } from 'react';
import RDCharacter from './RDCharacter';
import './RDWidget.css';

/**
 * RDWidget
 *
 * Floating chat widget for the RetroDayz homepage. Collapsed by default
 * (small RD character bottom-right, idle-animating). Click to expand
 * into a chat panel.
 *
 * Wires RD's visual state to what's actually happening:
 *   - idle:      collapsed, or expanded with no input focus / no messages pending
 *   - listening: input is focused
 *   - thinking:  waiting on a response from the backend
 *   - talking:   most recent RD message is still "fresh" (briefly after it arrives)
 *
 * `onSend` is a prop so the backend integration can be swapped in later
 * without touching this component — pass an async function that takes
 * the user's message + conversation history and resolves to
 * { reply: string, products: [...] }.
 */

const TALKING_DURATION_MS = 1500;

const GREETINGS = [
  "Hey, I'm RD! I can help you find something for any occasion — Father's Day, birthdays, you name it. Tell me what you need.",
  "Hey, RD here! Whether it's a gift or a treat for yourself, I've got you covered. What are you after?",
  "Welcome! I'm RD, your retro-finding sidekick. Tell me what you need and I'll help you find it.",
  "Hey there! I'm RD — birthdays, holidays, or just because. Tell me what you need.",
  "Got a budget in mind? Tell me your price range and what you're after, and I'll find something that fits.",
  "Big movie fan? Tell me the film or character and I'll see what we've got.",
  "Gamer at heart? Tell me the game, console, or character and let's find your next pickup.",
];

function getRandomGreeting() {
  return GREETINGS[Math.floor(Math.random() * GREETINGS.length)];
}

export default function RDWidget({ onSend }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [isTalking, setIsTalking] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState([
    { role: 'rd', text: getRandomGreeting() },
  ]);

  const talkingTimeoutRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    return () => {
      if (talkingTimeoutRef.current) clearTimeout(talkingTimeoutRef.current);
    };
  }, []);

  function getCharacterState() {
    if (isThinking) return 'thinking';
    if (isTalking) return 'talking';
    if (isInputFocused) return 'listening';
    return 'idle';
  }

  async function handleSend() {
    const trimmed = inputValue.trim();
    if (!trimmed || isThinking) return;

    const nextMessages = [...messages, { role: 'user', text: trimmed }];
    setMessages(nextMessages);
    setInputValue('');
    setIsThinking(true);

    try {
      const result = onSend
          ? await onSend(trimmed, nextMessages)
          : { reply: "I'm not wired up to the backend yet, but I heard you!", products: [] };

      setMessages((prev) => [
        ...prev,
        { role: 'rd', text: result.reply, products: result.products || [] },
      ]);
      setIsTalking(true);
      talkingTimeoutRef.current = setTimeout(() => setIsTalking(false), TALKING_DURATION_MS);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: 'rd', text: "Hmm, couldn't quite get that. Mind trying again?" },
      ]);
    } finally {
      setIsThinking(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  if (!isOpen) {
    return (
        <button
            type="button"
            className="rd-bubble"
            onClick={() => setIsOpen(true)}
            aria-label="Open RD, RetroDayz's shopping assistant"
        >
          <RDCharacter state="idle" size={56} />
        </button>
    );
  }

  return (
      <div className="rd-panel" role="dialog" aria-label="RD, RetroDayz's shopping assistant">
        <div className="rd-panel-header">
          <RDCharacter state={getCharacterState()} size={40} />
          <span className="rd-panel-title">RD</span>
          <button
              type="button"
              className="rd-close-btn"
              onClick={() => setIsOpen(false)}
              aria-label="Close chat"
          >
            &times;
          </button>
        </div>

        <div className="rd-messages">
          {messages.map((m, i) => (
              <div key={i} className={`rd-message rd-message-${m.role}`}>
                <p>{m.text}</p>
                {m.products && m.products.length > 0 && (
                    <div className="rd-products">
                      {m.products.map((p) => (
                          <a key={p.id} href={p.url} className="rd-product-card">
                            <img src={p.image} alt={p.name} />
                            <span className="rd-product-name">{p.name}</span>
                            <span className="rd-product-price">{p.price}</span>
                          </a>
                      ))}
                    </div>
                )}
              </div>
          ))}
          {isThinking && (
              <div className="rd-message rd-message-rd">
                <p className="rd-thinking-dots">...</p>
              </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="rd-input-row">
          <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onFocus={() => setIsInputFocused(true)}
              onBlur={() => setIsInputFocused(false)}
              onKeyDown={handleKeyDown}
              placeholder="Looking for something retro..."
              disabled={isThinking}
          />
          <button type="button" onClick={handleSend} disabled={isThinking || !inputValue.trim()}>
            Send
          </button>
        </div>
      </div>
  );
}